/**
 * The print endpoints, as the frontend sees them (PHASE-0.md §2.6).
 *
 * Mirrors `api/src/Continuum.Api/Print/PrintEndpoints.cs`. Both documents return the
 * **same** payload the overview screen reads — see that file for why there is one query
 * rather than two: paper in a clinician's hand must not be able to disagree with the
 * screen beside it about an allergy or a dose.
 *
 * **A fetch here is audited as `print`, not as `read`.** The row is written from the
 * route before the handler runs and before anything is serialised, and it names which of
 * the two documents was asked for. So this is called once, when a clinician opens a print
 * view — never on a hover, a prefetch or a poll, and there is no refresh timer, for the
 * same reason `fetchPatientOverview` has none. `AGENTS.md` §4 audits prints separately
 * because paper leaves the box: it is not revoked when the outage ends and it is the one
 * disclosure the system cannot see again.
 */

import { unauthenticated } from '../auth/sessionExpiry'
import type { PatientOverview } from '../patient/patientOverviewApi'

/**
 * Which document. The two strings the route constraint accepts, and nothing else.
 *
 * Kept as a union rather than a bare `string` because the value travels into an
 * append-only, hash-chained audit log by way of the URL. A typo here is a 404 from
 * routing rather than a row nobody can group by, which is the behaviour
 * `PrintEndpoints` is built for — but a compile error is cheaper still.
 */
export type PrintDocument = 'administration-record' | 'wristband' | 'record-extract'

/** Every document, in the order the patient screen offers them. */
export const PRINT_DOCUMENTS: readonly PrintDocument[] = [
  'administration-record',
  'wristband',
  // The journalutdrag (DECISIONS.md #51): the server sends every signed note for this one.
  'record-extract',
]

/** Why a print view could not be shown. Codes, so the catalogue does the words. */
export type PrintFailure = 'not-found' | 'unavailable'

export class PrintError extends Error {
  readonly failure: PrintFailure

  constructor(failure: PrintFailure) {
    super(`Print document failed: ${failure}`)
    this.name = 'PrintError'
    this.failure = failure
  }
}

/**
 * Reads one patient's record for printing.
 *
 * `document` decides what the audit row says and nothing else about the response — the
 * difference between a signeringslista and a wristband is entirely in how this module's
 * components lay the same record out.
 */
export async function fetchPrintDocument(
  patientId: string,
  document: PrintDocument,
  signal: AbortSignal,
): Promise<PatientOverview> {
  let response: Response

  try {
    response = await fetch(
      `/api/patients/${encodeURIComponent(patientId)}/print/${document}`,
      { signal, headers: { Accept: 'application/json' } },
    )
  } catch (cause) {
    // An aborted fetch is the clinician having moved on, not a failure. Re-thrown so
    // the caller can tell the two apart rather than showing an error for a screen
    // nobody is looking at.
    if (signal.aborted) {
      throw cause
    }
    throw new PrintError('unavailable')
  }

  if (response.status === 404) {
    throw new PrintError('not-found')
  }

  // Before the general case, because a 401 is not an unavailable box: it means the session
  // has ended, and `SessionGate` is about to show the login screen. Reported rather than
  // rendered — see `auth/sessionExpiry.ts`. The throw still happens, so this screen does not
  // sit half-updated in the moment before it unmounts.
  unauthenticated(response)

  if (!response.ok) {
    throw new PrintError('unavailable')
  }

  return (await response.json()) as PatientOverview
}
